<div class="section-divider d-none d-md-flex align-items-center gap-3 mb-2">
  <div class="flex-grow-1 border-top"></div>
  <span class="fw-semibold small text-uppercase text-muted px-1" style="letter-spacing:.08em;">⚡ Energie</span>
  <div class="flex-grow-1 border-top"></div>
</div>

<!-- Live Solar Cards -->
<div class="section-label d-flex align-items-center gap-2 mb-2">
  <span class="badge bg-danger" style="font-size:.7rem;letter-spacing:.05em;"><span [style.opacity]="liveDotOn ? '1' : '0'">●</span> LIVE</span>
  <span class="fw-semibold small text-uppercase tracking-wide">{{ 'AKTUELL' | translate }}</span>
  <span *ngIf="solarUpdatedAt" class="text-muted" style="font-size:.65rem;">· {{ 'UPDATED_AGO' | translate:{time: updatedAgo(solarUpdatedAt)} }}</span>
  <button *ngIf="solarHistory.length" class="btn btn-sm btn-outline-secondary ms-auto" style="font-size:.7rem;padding:1px 8px;" (click)="openSolarChart.emit()">📈 24h</button>
</div>

<!-- Shared solar chart template: context {mode, label, full} -->
<ng-template #solarChartTpl let-mode="mode" let-label="label" let-full="full">
  <div class="fw-semibold small mb-1">{{ label }}</div>
  <app-sparkline-chart
    [solarPoints]="solarHistory"
    [solarMode]="$any(mode)"
    [darkMode]="darkMode"
    [expanded]="full"
    style="display:block;">
  </app-sparkline-chart>
  <div *ngIf="full && solarHistory.length" class="d-flex justify-content-between mt-1" style="font-size:.65rem;">
    <span class="text-muted">{{ fmtW(solarMinMax($any(mode)).min) }}</span>
    <span class="text-muted">24h</span>
    <span class="text-muted">{{ fmtW(solarMinMax($any(mode)).max) }}</span>
  </div>
</ng-template>

<!-- Individual popup/tap templates per metric -->
<ng-template #pvPopup>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'pvYield', label:'☀️ ' + ('PV_ERTRAG' | translate), full:false}"></ng-container>
</ng-template>
<ng-template #pvTapTpl>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'pvYield', label:'☀️ ' + ('PV_ERTRAG' | translate), full:true}"></ng-container>
</ng-template>

<ng-template #battPopup>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'battery', label:'🔋 ' + ('AKKU' | translate), full:false}"></ng-container>
</ng-template>
<ng-template #battTapTpl>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'battery', label:'🔋 ' + ('AKKU' | translate), full:true}"></ng-container>
</ng-template>

<ng-template #gridPopup>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'grid', label:'🔌 ' + ('NETZBEZUG' | translate), full:false}"></ng-container>
</ng-template>
<ng-template #gridTapTpl>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'grid', label:'🔌 ' + ('NETZBEZUG' | translate), full:true}"></ng-container>
</ng-template>

<ng-template #consPopup>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'consumption', label:'🏠 ' + ('VERBRAUCH' | translate), full:false}"></ng-container>
</ng-template>
<ng-template #consTapTpl>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'consumption', label:'🏠 ' + ('VERBRAUCH' | translate), full:true}"></ng-container>
</ng-template>

<div class="row g-2 g-sm-3 mb-3" *ngIf="solar" [class.section-flash]="solarFlash">

  <!-- PV Ertrag -->
  <div class="col-6 col-lg-3">
    <app-popup-card cardClass="h-100 border-warning" popupMaxWidth="min(92vw, 1100px)"
                    [tapTpl]="solarHistory.length >= 2 ? pvTapTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">☀️</div>
            <div class="min-w-0">
              <div class="text-muted small">{{ 'PV_ERTRAG' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-warning text-truncate">{{ formatW(solar.pvYieldW) }}</div>
              <div *ngIf="vs.sunrise" class="text-muted text-truncate" style="font-size:.65rem;">🌅 {{ vs.sunrise }} · 🌇 {{ vs.sunset }}</div>
            </div>
          </div>
          <app-sparkline-chart *ngIf="solarHistory.length >= 2" [solarPoints]="solarHistory" [solarMode]="'pvYield'" [darkMode]="darkMode" style="display:block;margin-top:6px;"></app-sparkline-chart>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Akku -->
  <div class="col-6 col-lg-3">
    <app-popup-card cardClass="h-100 border-success" popupMaxWidth="min(92vw, 1100px)"
                    [tapTpl]="solarHistory.length >= 2 ? battTapTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0"
                 [class.battery-charging]="solar.batteryDischargeW < 0"
                 [class.battery-discharging]="solar.batteryDischargeW > 0">
              <ng-container *ngIf="solar.batteryDischargeW < 0">⚡</ng-container>
              <ng-container *ngIf="solar.batteryDischargeW > 0">🪫</ng-container>
              <ng-container *ngIf="solar.batteryDischargeW === 0">🔋</ng-container>
            </div>
            <div class="flex-grow-1 min-w-0">
              <div class="text-muted small">{{ 'AKKU' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-truncate"
                   [class.text-success]="solar.batteryDischargeW <= 0"
                   [class.text-danger]="solar.batteryDischargeW > 0">
                {{ formatW(-solar.batteryDischargeW) }}
              </div>
              <div class="progress mt-1" style="height:5px;">
                <div class="progress-bar"
                     [class.bg-success]="solar.batteryDischargeW === 0"
                     [class.progress-bar-charging]="solar.batteryDischargeW < 0"
                     [class.progress-bar-discharging]="solar.batteryDischargeW > 0"
                     [style.width.%]="solar.batteryPercent"></div>
              </div>
              <div class="text-muted mt-1 text-truncate" style="font-size:.72rem;">
                {{ solar.batteryPercent }} %
                <ng-container *ngIf="solar.batteryCapacityWh">
                  &nbsp;·&nbsp;{{ (solar.batteryPercent / 100 * solar.batteryCapacityWh / 1000).toFixed(1) }}&thinsp;/&thinsp;{{ (solar.batteryCapacityWh / 1000).toFixed(0) }}&thinsp;kWh
                </ng-container>
                <ng-container *ngIf="batteryRuntime(solar) as rt">&nbsp;·&nbsp;{{ rt }}</ng-container>
              </div>
            </div>
          </div>
          <app-sparkline-chart *ngIf="solarHistory.length >= 2" [solarPoints]="solarHistory" [solarMode]="'battery'" [darkMode]="darkMode" style="display:block;margin-top:6px;"></app-sparkline-chart>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Netzbezug -->
  <div class="col-6 col-lg-3">
    <app-popup-card cardClass="h-100 border-info" popupMaxWidth="min(92vw, 1100px)"
                    [tapTpl]="solarHistory.length >= 2 ? gridTapTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🔌</div>
            <div class="min-w-0">
              <div class="text-muted small">{{ 'NETZBEZUG' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-truncate"
                   [class.text-danger]="solar.gridConsumptionW > 0"
                   [class.text-success]="solar.gridConsumptionW <= 0">
                {{ formatW(solar.gridConsumptionW) }}
              </div>
              <div class="text-muted small">{{ (solar.gridConsumptionW > 0 ? 'BEZUG' : 'EINSPEISUNG') | translate }}</div>
            </div>
          </div>
          <app-sparkline-chart *ngIf="solarHistory.length >= 2" [solarPoints]="solarHistory" [solarMode]="'grid'" [darkMode]="darkMode" style="display:block;margin-top:6px;"></app-sparkline-chart>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Verbrauch -->
  <div class="col-6 col-lg-3">
    <app-popup-card cardClass="h-100 border-primary" popupMaxWidth="min(92vw, 1100px)"
                    [tapTpl]="solarHistory.length >= 2 ? consTapTpl : undefined">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🏠</div>
            <div class="min-w-0">
              <div class="text-muted small">{{ 'VERBRAUCH' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-primary text-truncate">{{ formatW(solar.totalConsumptionW) }}</div>
            </div>
          </div>
          <app-sparkline-chart *ngIf="solarHistory.length >= 2" [solarPoints]="solarHistory" [solarMode]="'consumption'" [darkMode]="darkMode" style="display:block;margin-top:6px;"></app-sparkline-chart>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Today's autarky summary -->
  <div class="col-6 col-lg-3" *ngIf="todayStat">
    <div class="card h-100" [class.border-success]="todayStat.autarkiePercent >= 80"
         [class.border-warning]="todayStat.autarkiePercent >= 50 && todayStat.autarkiePercent < 80"
         [class.border-danger]="todayStat.autarkiePercent < 50">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <div class="card-icon fs-2 flex-shrink-0">🏡</div>
        <div class="min-w-0 flex-grow-1">
          <div class="d-flex align-items-baseline gap-1">
            <div class="text-muted small">{{ 'HEUTE_AUTARKIE' | translate }}</div>
            <div class="text-muted" style="font-size:.6rem;">{{ 'HEUTE_SEIT' | translate }}</div>
          </div>
          <div class="card-value fs-5 fw-bold text-truncate"
               [class.text-success]="todayStat.autarkiePercent >= 80"
               [class.text-warning]="todayStat.autarkiePercent >= 50 && todayStat.autarkiePercent < 80"
               [class.text-danger]="todayStat.autarkiePercent < 50">
            {{ Math.floor(todayStat.autarkiePercent) }}&thinsp;%
          </div>
          <div class="text-muted text-truncate d-none d-xl-block" style="font-size:.68rem;line-height:1.5;">
            ⚡&thinsp;{{ formatKwh(todayStat.consumptionKwh) }}
            &nbsp;·&nbsp;🔌&thinsp;{{ formatKwh(todayStat.gridImportKwh) }}
          </div>
        </div>
      </div>
      <div class="card-footer d-xl-none text-muted text-center p-1" style="font-size:.62rem;">
        ⚡&thinsp;{{ formatKwh(todayStat.consumptionKwh) }} &nbsp;·&nbsp; 🔌&thinsp;{{ formatKwh(todayStat.gridImportKwh) }}
      </div>
    </div>
  </div>

</div>

<div *ngIf="!solar" class="row g-2 g-sm-3 mb-3">
  <div class="col-6 col-lg-3" *ngFor="let _ of [0,1,2,3]">
    <div class="card h-100">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <app-skeleton width="2rem" height="2rem" radius="6px" [darkMode]="darkMode" style="flex-shrink:0;"></app-skeleton>
        <div style="flex:1;min-width:0;">
          <app-skeleton width="60%" height=".75rem" radius="4px" [darkMode]="darkMode" style="display:block;margin-bottom:6px;"></app-skeleton>
          <app-skeleton width="80%" height="1.4rem" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
        </div>
      </div>
    </div>
  </div>
</div>
